import {
  // 获取Ranks数据的原接口方法
  fetchStoreRankMoreRanks as fetchStoreRankMoreRanksOriginal,
  fetchStoreRankMoreRanksMobile,
  // 获取Trends数据的原接口方法
  fetchStoreRankMoreTrends as fetchStoreRankMoreTrendsOriginal,
} from '@/pages/intelligence/services/storeRank';
import {
  handleStoreRankSourceReq,
  handleStoreRankMetricRank,
  handleStoreRankSource,
} from '@/pages/intelligence/_config/storeRank/utils';

import type {
  IStoreRankMoreCoreFetchParams,
  IStoreRankMoreCoreTrend,
  IStoreRankMoreCoreTrendHistory,
} from '@/pages/intelligence/types/storeRank';

// store rank 0 是无效数据，所以在接口交互这里  统一把0处理成无效数据 空字符串
const handleMetricValue = (payload?: number | string): string => {
  if (!payload) return '';
  if (payload === '0') return '';
  return payload as string;
};

/**
 * 统一处理 个别字段值的特殊转换，提供给接口入参
 * 1、Nintendo平台的 regions 固定传global，因为这个平台没有国家筛选器
 * @param params 原页面参数
 * @returns 处理的，接口入参
 */
const handleParams = (params: IStoreRankMoreCoreFetchParams): IStoreRankMoreCoreFetchParams => {
  // const { platform } = analysisPlatformTopInfo(params?.topchart_name);
  return {
    ...params,
    topchart_name: handleStoreRankSourceReq(params?.topchart_name, params?.granularity),
    order_source: handleStoreRankSourceReq(params?.order_source, params?.granularity),
  };
};

const handleRanksRes = (res: any): any => {
  const metricListTmp = res?.data?.metric_list?.map((item: string) =>
    handleStoreRankMetricRank(item),
  );
  const listTmp = res?.data?.list?.map((item: any) => {
    const sourceMetricListTmp = item?.source_metric_list?.map((subItem: any) => {
      const valueTmp = handleMetricValue(subItem?.value_string);
      return {
        ...subItem,
        data_source: handleStoreRankSource(subItem?.data_source),
        value: valueTmp,
      };
    });
    return {
      ...item,
      source_metric_list: sourceMetricListTmp || [],
    };
  });

  return {
    ...res,
    data: {
      ...res?.data,
      metric_list: metricListTmp || [],
      list: listTmp || [],
    },
    req: {
      ...res?.req,
      order_source: handleStoreRankSource(res?.req?.order_source),
    },
  };
};

const handleTrendsHistoriesRes = (trends: IStoreRankMoreCoreTrend[]): IStoreRankMoreCoreTrend[] => {
  if (!Array.isArray(trends)) return [];
  // 至少有一个游戏 有数据的日期集合
  // const dates: Set<String> = new Set();
  // for (const trend of trends) {
  //   const trendHistories = trend?.history;
  //   if (!Array.isArray(trendHistories)) continue;
  //   for (const trendHistory of trendHistories) {
  //     const date = trendHistory?.time;
  //     const metricValue = trendHistory?.value;
  //     if (date && metricValue && metricValue !== '0') {
  //       dates.add(date);
  //     }
  //   }
  // }
  // return trends;
  // 过滤掉所有游戏都没有数据的日期------取消过滤空值时间
  return trends?.map((item) => {
    const histories: IStoreRankMoreCoreTrendHistory[] = item?.history
      // ?.filter((item) => dates?.has(item?.time))
      ?.map((item) => {
        const valueTmp = handleMetricValue(item?.value);
        return {
          ...item,
          source: handleStoreRankSource(item?.source),
          value: valueTmp,
        };
      });
    return {
      ...item,
      history: histories,
    };
  });
};

const handleTrendsRes = (res: any): any => {
  // console.log('resres', res);
  const timeListRes = res?.data?.time_list;
  if (Object.prototype.toString.call(timeListRes) !== '[object Object]') {
    return {
      ...res,
      data: {
        ...res?.data,
        time_list: {},
      },
    };
  }
  const timeListTmp = Object.entries(res?.data?.time_list)?.reduce((current, next) => {
    const [key, value] = next;
    if (!key) return current;
    const keyTmp = handleStoreRankSource(key);
    const trends = (value || []) as IStoreRankMoreCoreTrend[];
    const histories = handleTrendsHistoriesRes(trends);
    // console.log('keyTmp', keyTmp);
    // console.log('trends', trends);
    // console.log('histories', histories);
    return {
      ...current,
      [keyTmp]: histories,
    };
  }, {});
  return {
    ...res,
    data: {
      ...res?.data,
      time_list: timeListTmp,
    },
  };
};

/**
 * pc store rank下钻页 搜索某个游戏时，调整order_source字段入参
 * @param params - 接口入参
 * @return 接口入参
 */
const handlePCAndConsoleParamsWhenSearch = (params: IStoreRankMoreCoreFetchParams): IStoreRankMoreCoreFetchParams => {
  if (!params) {
    return {} as unknown as IStoreRankMoreCoreFetchParams;
  }

  if (params?.entity_type !== 'pc') {
    return { ...params };
  }

  if (!params?.search_ids?.length) {
    return { ...params };
  }

  return { ...params, order_source: params?.topchart_name || '' };
};

/**
 * 对获取Ranks数据的原接口方法 增加统一特殊字段值处理的 接口方法，页面调用这个方法
 * @param params 接口参数
 * @param config 可选配置，包含 signal 等
 * @returns 获取Ranks数据接口的返回值，页面直接使用
 */
export const fetchStoreRankMoreRanks = async (
  params: IStoreRankMoreCoreFetchParams,
  config?: { [key: string]: any; },
): ReturnType<typeof fetchStoreRankMoreRanksOriginal> => {
  const processedParams = handleParams(params);

  // mobile 使用 fetchStoreRankMoreRanksMobile，pc 和 console 使用 fetchStoreRankMoreRanksOriginal
  const isMobile = processedParams?.entity_type === 'mobile';
  const res = isMobile
    ? await fetchStoreRankMoreRanksMobile(processedParams, config)
    : await fetchStoreRankMoreRanksOriginal(handlePCAndConsoleParamsWhenSearch(processedParams), config);
  return handleRanksRes(res);
};

/**
 * 对获取Trends数据的原接口方法 增加统一特殊字段值处理的 接口方法，页面调用这个方法
 * @param args handleParams的参数
 * @returns 获取Trends数据接口的返回值，页面直接使用
 */
export const fetchStoreRankMoreTrends = async (
  ...args: Parameters<typeof handleParams>
): ReturnType<typeof fetchStoreRankMoreTrendsOriginal> => {
  const params = handleParams(...args);
  const res = await fetchStoreRankMoreTrendsOriginal(params);
  return handleTrendsRes(res);
};
